import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';
import { usePWAInstall } from '@/hooks/use-pwa-install';
import { usePWAUpdate } from '@/hooks/use-pwa-update';

const BANNER_HEIGHT = '80px';
const IOS_BANNER_HEIGHT = '108px';

// Routes that use viewport-locked (h-screen) layouts where a fixed bottom
// banner would overlap critical UI (e.g. the field-inspection map's filter
// pills). `document.body.style.paddingBottom` doesn't push 100vh containers
// up, so we just suppress the banner entirely on these routes.
const FULLSCREEN_ROUTE_PATTERNS: RegExp[] = [
  /\/field-inspection(\/|$)/,
];

function isFullscreenRoute(pathname: string): boolean {
  return FULLSCREEN_ROUTE_PATTERNS.some((re) => re.test(pathname));
}

// Approximation of the iOS Safari Share glyph: open-top box with up arrow.
// Lucide's `Upload` (tray + arrow) reads as "upload", not "share" — users
// scanning Safari's toolbar would miss it.
function IOSShareGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      aria-label="Safari Share button"
    >
      <path d="M9 9 H7 a2 2 0 0 0 -2 2 v8 a2 2 0 0 0 2 2 h10 a2 2 0 0 0 2 -2 v-8 a2 2 0 0 0 -2 -2 h-2" />
      <path d="M12 3 v12" />
      <path d="m8 7 4-4 4 4" />
    </svg>
  );
}

export function InstallBanner() {
  const { isInstallable, isIOSInstallable, isInstalled, install, dismiss } = usePWAInstall();
  const { hasUpdate } = usePWAUpdate();
  const location = useLocation();

  const routeAllowsBanner = !isFullscreenRoute(location.pathname);
  const showAndroid = routeAllowsBanner && isInstallable && !isInstalled && !hasUpdate;
  const showIOS = routeAllowsBanner && !showAndroid && isIOSInstallable && !isInstalled && !hasUpdate;
  const shouldShow = showAndroid || showIOS;

  useEffect(() => {
    if (shouldShow) {
      document.body.style.paddingBottom = showIOS ? IOS_BANNER_HEIGHT : BANNER_HEIGHT;
      return () => {
        document.body.style.paddingBottom = '';
      };
    }
  }, [shouldShow, showIOS]);

  if (!shouldShow) {
    return null;
  }

  if (showIOS) {
    return (
      <aside
        role="complementary"
        aria-label="Install application on iOS"
        className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-primary text-primary-foreground shadow-lg border-t animate-in slide-in-from-bottom-5"
      >
        <div className="container mx-auto flex items-start gap-3">
          <div className="hidden sm:block p-2 bg-primary-foreground/10 rounded-full shrink-0">
            <Smartphone className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">Install SmartExtract</p>
            <p className="text-xs text-primary-foreground/90 mt-0.5 flex flex-wrap items-center gap-x-1">
              <span>In Safari, tap</span>
              <IOSShareGlyph className="h-4 w-4 inline-block" />
              <span>then</span>
              <span className="font-medium">Add to Home Screen</span>
            </p>
            <p className="text-[11px] text-primary-foreground/70 mt-0.5">
              Don't see Share? Open this page directly in Safari.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={dismiss}
            aria-label="Dismiss install banner"
            className="h-7 w-7 shrink-0 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside
      role="complementary"
      aria-label="Install application"
      className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-primary text-primary-foreground shadow-lg border-t animate-in slide-in-from-bottom-5"
    >
      <div className="container mx-auto">
        {/* Mobile: stacked layout */}
        <div className="flex flex-col gap-2 sm:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" aria-hidden="true" />
              <p className="font-medium text-sm">Install SmartExtract</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismiss}
              aria-label="Dismiss install banner"
              className="h-7 w-7 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={install}
            className="w-full gap-1"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Install App
          </Button>
        </div>

        {/* Desktop: horizontal layout */}
        <div className="hidden sm:flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-foreground/10 rounded-full">
              <Smartphone className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <p className="font-medium text-sm">Install SmartExtract</p>
              <p className="text-xs text-primary-foreground/80">
                Add to your home screen for quick access
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={install}
              className="gap-1"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Install
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={dismiss}
              aria-label="Dismiss install banner"
              className="h-8 w-8 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
}
